import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useToast } from "./Toast";
import Modal from "./Modal";
import ConfirmDialog from "./ConfirmDialog";
import EmbedCodeGenerator from "./EmbedCodeGenerator";
import {
  EditIcon,
  DeleteIcon,
  CodeIcon,
  ChartIcon,
  BookIcon,
  ChatIcon,
  MoreVerticalIcon,
} from "./icons";
import { sanitizeWidgetName, formatDate } from "../utils/sanitize";
import useConfirm from "../hooks/useConfirm";
import errorTracker from "../utils/errorTracker";
import "./WidgetCard.css";

const WidgetCard = ({ widget, onDelete, onViewAnalytics }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { confirmState, confirm } = useConfirm();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const moreMenuRef = useRef(null);
  const dropdownItemsRef = useRef([]);

  // Number of menu items (stable constant)
  const MENU_ITEM_COUNT = 2;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
        setFocusedIndex(-1);
      }
    };

    if (showMoreMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showMoreMenu]);

  // Keyboard navigation for dropdown
  const handleDropdownKeyDown = useCallback(
    (event) => {
      if (!showMoreMenu) return;

      const executeAction = (index) => {
        if (index === 0) {
          navigate(`/widgets/${widget.id}/knowledge`);
        } else if (index === 1) {
          navigate(`/widgets/${widget.id}/conversations`);
        }
        setShowMoreMenu(false);
        setFocusedIndex(-1);
      };

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % MENU_ITEM_COUNT);
          break;
        case "ArrowUp":
          event.preventDefault();
          setFocusedIndex(
            (prev) => (prev - 1 + MENU_ITEM_COUNT) % MENU_ITEM_COUNT,
          );
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < MENU_ITEM_COUNT) {
            executeAction(focusedIndex);
          }
          break;
        case "Escape":
          event.preventDefault();
          setShowMoreMenu(false);
          setFocusedIndex(-1);
          break;
        case "Tab":
          setShowMoreMenu(false);
          setFocusedIndex(-1);
          break;
        default:
          break;
      }
    },
    [showMoreMenu, focusedIndex, navigate, widget.id],
  );

  // Focus management for dropdown items
  useEffect(() => {
    if (
      showMoreMenu &&
      focusedIndex >= 0 &&
      dropdownItemsRef.current[focusedIndex]
    ) {
      dropdownItemsRef.current[focusedIndex].focus();
    }
  }, [showMoreMenu, focusedIndex]);

  // Reset focused index when dropdown opens
  useEffect(() => {
    if (showMoreMenu) {
      setFocusedIndex(0);
    }
  }, [showMoreMenu]);

  const handleEdit = () => {
    navigate(`/widgets/${widget.id}/edit`);
  };

  const handleViewEmbed = () => {
    setShowEmbedModal(true);
  };

  const handleDelete = async () => {
    const safeName = sanitizeWidgetName(widget.display_name || widget.name);

    const confirmed = await confirm({
      title: "Delete Widget",
      message: `Are you sure you want to delete "${safeName}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      setIsDeleting(true);
      try {
        await onDelete(widget.id);
        toast.success("Widget deleted successfully");
      } catch (err) {
        errorTracker.captureException(err, {
          context: "deleteWidget",
          widgetId: widget.id,
        });
        toast.error(
          err.message || "Failed to delete widget. Please try again.",
        );
        setIsDeleting(false);
      }
      // Note: Don't set isDeleting to false on success - card will be removed from list
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      active: "Active",
      paused: "Paused",
      suspended: "Suspended",
      inactive: "Inactive",
    };
    return statusTexts[status] || "Unknown";
  };

  const safeName = sanitizeWidgetName(widget.display_name || widget.name);

  return (
    <div className={`widget-card ${isDeleting ? "widget-card-deleting" : ""}`}>
      <div className="widget-card-header">
        <div className="widget-card-title-section">
          <h3 className="widget-card-title">{safeName}</h3>
          <span
            className={`widget-card-status widget-card-status-${widget.status || "inactive"}`}
          >
            {getStatusText(widget.status)}
          </span>
        </div>
        <div className="widget-card-actions">
          <button
            className="widget-card-action-btn"
            onClick={handleEdit}
            title="Edit widget"
            disabled={isDeleting}
            aria-label="Edit widget"
          >
            <EditIcon size={16} />
          </button>
          <button
            className="widget-card-action-btn widget-card-action-btn-danger"
            onClick={handleDelete}
            title="Delete widget"
            disabled={isDeleting}
            aria-label="Delete widget"
          >
            {isDeleting ? (
              <span className="widget-card-spinner"></span>
            ) : (
              <DeleteIcon size={16} />
            )}
          </button>
        </div>
      </div>

      <div className="widget-card-stats">
        <div className="widget-card-stat">
          <span className="widget-card-stat-label">Conversations</span>
          <span className="widget-card-stat-value">
            {widget.conversation_count || 0}
          </span>
        </div>
        <div className="widget-card-stat">
          <span className="widget-card-stat-label">Messages</span>
          <span className="widget-card-stat-value">
            {widget.message_count || 0}
          </span>
        </div>
        <div className="widget-card-stat">
          <span className="widget-card-stat-label">Last Used</span>
          <span className="widget-card-stat-value">
            {formatDate(widget.last_used_at)}
          </span>
        </div>
      </div>

      <div className="widget-card-footer">
        <button
          className="widget-card-btn widget-card-btn-primary"
          onClick={() => onViewAnalytics(widget.id)}
          disabled={isDeleting}
          title="View analytics"
        >
          <ChartIcon size={16} />
          Analytics
        </button>
        <button
          className="widget-card-btn widget-card-btn-secondary"
          onClick={handleViewEmbed}
          disabled={isDeleting}
          title="Get embed code"
        >
          <CodeIcon size={16} />
          Embed
        </button>
        <div className="widget-card-more-menu" ref={moreMenuRef}>
          <button
            className="widget-card-btn widget-card-btn-secondary widget-card-btn-icon"
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            onKeyDown={handleDropdownKeyDown}
            disabled={isDeleting}
            title="More actions"
            aria-label="More actions"
            aria-expanded={showMoreMenu}
            aria-haspopup="menu"
          >
            <MoreVerticalIcon size={16} />
          </button>
          {showMoreMenu && (
            <div
              className="widget-card-dropdown"
              role="menu"
              aria-label="Widget actions menu"
              onKeyDown={handleDropdownKeyDown}
            >
              <button
                ref={(el) => (dropdownItemsRef.current[0] = el)}
                className={`widget-card-dropdown-item ${focusedIndex === 0 ? "focused" : ""}`}
                role="menuitem"
                tabIndex={focusedIndex === 0 ? 0 : -1}
                onClick={() => {
                  navigate(`/widgets/${widget.id}/knowledge`);
                  setShowMoreMenu(false);
                  setFocusedIndex(-1);
                }}
              >
                <BookIcon size={16} />
                Knowledge Sources
              </button>
              <button
                ref={(el) => (dropdownItemsRef.current[1] = el)}
                className={`widget-card-dropdown-item ${focusedIndex === 1 ? "focused" : ""}`}
                role="menuitem"
                tabIndex={focusedIndex === 1 ? 0 : -1}
                onClick={() => {
                  navigate(`/widgets/${widget.id}/conversations`);
                  setShowMoreMenu(false);
                  setFocusedIndex(-1);
                }}
              >
                <ChatIcon size={16} />
                Conversations
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        title="Widget Embed Code"
        size="large"
      >
        <EmbedCodeGenerator
          widgetId={widget.id}
          widgetKey={widget.widget_key}
        />
      </Modal>

      <ConfirmDialog {...confirmState} />
    </div>
  );
};

WidgetCard.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    display_name: PropTypes.string,
    status: PropTypes.string,
    widget_key: PropTypes.string,
    conversation_count: PropTypes.number,
    message_count: PropTypes.number,
    last_used_at: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewAnalytics: PropTypes.func.isRequired,
};

export default WidgetCard;
